import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { THEME } from '@/lib/theme';
import { useT } from '@/lib/i18n/useT';
import type { StationInventoryTotals } from '@/lib/api/types';

interface InventoryDonutProps {
  totals: StationInventoryTotals;
  height?: number;
}

export function InventoryDonut({ totals, height = 240 }: InventoryDonutProps) {
  const t = useT();
  if (totals.quantity === 0) {
    return (
      <p className="py-8 text-center text-sm text-[var(--color-fg-muted)]">
        {t('stations.detail.dashboardEmpty')}
      </p>
    );
  }

  const data = [
    { name: t('stations.detail.totalUsed'), value: totals.used, color: THEME.accent },
    {
      name: t('stations.detail.totalRemaining'),
      value: totals.available,
      color: THEME.success,
    },
  ];
  const usedPct = Math.round((totals.used / totals.quantity) * 100);

  return (
    <div className="relative" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            innerRadius="60%"
            outerRadius="85%"
            paddingAngle={2}
            dataKey="value"
            stroke="var(--color-bg-surface)"
          >
            {data.map((d, i) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              background: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border-default)',
              borderRadius: 8,
              fontSize: 12,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-[var(--color-fg-primary)]">
          {usedPct}%
        </span>
        <span className="text-xs uppercase tracking-wide text-[var(--color-fg-muted)]">
          {t('stations.detail.totalUsed')}
        </span>
        <span className="mt-1 text-xs text-[var(--color-fg-subtle)]">
          {totals.used} / {totals.quantity}
        </span>
      </div>
    </div>
  );
}
